import { ContactKey, ExtensionKey } from 'flow/types/EntityKeys';
import { AppDispatch } from 'flow/types/Redux';
import { State } from 'flow/types/State';
import {
    ContactFields,
    ContactFieldsSimple,
    ContactFieldsNumberMap,
    NumberContactsDTO,
} from 'flow/types/Contact';
import { updateEditableContact } from 'flow/actions/contact-actions';
import {
    selectEditableContactDTO,
    selectEditableContactFields,
} from 'flow/selectors/contact-selectors';

/**
 * Type for all contact field updates
 * - String fields (firstname, lastname, email, etc.) accept string values
 * - Number fields (mobile, alttelephone1, etc.) accept TypedMap number objects
 */
export type ContactFieldUpdates = {
    [K in keyof ContactFieldsSimple]?: string;
} & {
    [K in keyof ContactFieldsNumberMap]?: NumberContactsDTO['number'];
};

/**
 * About selector selectExtensionKeyByContactKey below:
 * It's a selector to find the extension key that matches a given contact key
 * Since this at the time of writing is used in an action only, we don't memoize it.
 * If we were to use the selector in a component, it might be a good candidate for memoization,
 * so that we don't have to iterate over all extensions every time there's an update in the Redux store.
 */
const selectExtensionKeyByContactKey = (state: State, contactKey: ContactKey) =>
    state.extensions
        .get('extensions')
        ?.find(
            (extension) => extension.getIn(['contact', 'key']) === contactKey,
        )
        ?.get('key');

/**
 * Runtime check to determine if a field is a number field by inspecting its fieldType property
 * This eliminates the need for a hardcoded list of number fields
 */
const isNumberMapField = (
    contactFields: ReturnType<typeof selectEditableContactFields>,
    fieldKey: keyof ContactFields,
) => contactFields.getIn([fieldKey, 'fieldType']) === 'NUMBER';

/**
 * Updates multiple contact fields in an editableContactDTO
 *
 * @param contactKey - The contact key to update
 * @param updateValues - An object with field names as keys and new values
 * @param extensionKey - The ExtensionKey associated with the contact (optional)
 * @param handleSuccess - Callback function to execute on successful update (optional)
 *
 * @example
 * ```typescript
 * // Update string fields
 * dispatch(updateEditableContactFields(
 *   'contact-123',
 *   { firstname: 'John', lastname: 'Doe', email: 'john@example.com' }
 * ));
 *
 * // Update number fields
 * dispatch(updateEditableContactFields(
 *   'contact-123',
 *   { mobile: mobileNumberObject }
 * ));
 *
 * // Update both types
 * dispatch(updateEditableContactFields(
 *   'contact-123',
 *   {
 *     firstname: 'John',
 *     mobile: mobileNumberObject
 *   }
 * ));
 * ```
 */
export const updateEditableContactFields =
    (
        contactKey: ContactKey,
        updateValues: Partial<ContactFieldUpdates>,
        extensionKey?: ExtensionKey,
        handleSuccess: () => void = () => {},
    ) =>
    (dispatch: AppDispatch, getState: () => State) => {
        const state = getState();
        const contactFields = selectEditableContactFields(state, contactKey);

        if (!contactFields) {
            console.error(
                `No editable contact found for contact key: ${contactKey}`,
            );
            return;
        }

        const editableContactDTO = selectEditableContactDTO(state, contactKey);
        if (!editableContactDTO) return;

        // Using withMutations for better performance when making multiple changes
        const updatedContact = editableContactDTO.withMutations((mutable) => {
            // Apply all field updates
            Object.entries(updateValues).forEach(([fieldKey, value]) => {
                if (value !== undefined) {
                    // Number fields use 'number' property, string fields use 'value'
                    // Automatically detected by checking the field's fieldType property
                    const valuePath = isNumberMapField(contactFields, fieldKey)
                        ? 'number'
                        : 'value';

                    // Set the new value at the correct path
                    mutable.setIn(
                        ['contactFields', fieldKey, valuePath],
                        value,
                    );
                }
            });
        });

        // Dispatch the update
        dispatch(
            updateEditableContact(
                updatedContact,
                extensionKey ||
                    selectExtensionKeyByContactKey(state, contactKey),
                handleSuccess,
            ),
        );
    };

/**
 * Updates a single contact field in an editableContactDTO
 *
 * @param contactKey - The contact key to update
 * @param fieldKey - The field name to update (e.g., 'firstname', 'lastname', 'mobile')
 * @param value - The new value for the field (string for text fields, TypedMap for number fields)
 * @param extensionKey - The ExtensionKey associated with the contact (optional)
 * @param handleSuccess - Callback function to execute on successful update (optional)
 *
 * @example
 * ```typescript
 * // Update string field
 * dispatch(updateEditableContactField(
 *   'contact-123',
 *   'firstname',
 *   'John'
 * ));
 *
 * // Update number field
 * dispatch(updateEditableContactField(
 *   'contact-123',
 *   'mobile',
 *   mobileNumberObject
 * ));
 * ```
 */
export const updateEditableContactField =
    <K extends keyof ContactFieldUpdates>(
        contactKey: ContactKey,
        fieldKey: K,
        value: ContactFieldUpdates[K],
        extensionKey?: ExtensionKey,
        handleSuccess?: () => void,
    ) =>
    (dispatch: AppDispatch) => {
        dispatch(
            updateEditableContactFields(
                contactKey,
                { [fieldKey]: value } as Partial<ContactFieldUpdates>,
                extensionKey,
                handleSuccess,
            ),
        );
    };
